import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, LogIn } from "lucide-react";
import { loginResolver, type LoginForm } from "@shared/schemas/auth";
import { useLoginMutation } from "@shared/hooks/useAuthMutations";
import { Logo } from "@shared/components/Logo";

export function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading, error, reset } = useLoginMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginForm>(loginResolver);

  const onSubmit = (data: LoginForm) => {
    // Resetear cualquier error previo
    reset();

    // Ejecutar la mutación de login
    login({
      email: data.email,
      password: data.password,
    });
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Imagen de fondo */}
      <div className="absolute inset-0">
        <img
          src="/fondo-login.webp"
          alt="Almacén de materiales de construcción"
          className="w-full h-full object-cover"
        />
        {/* Overlay para mejorar la legibilidad */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
      </div>

      {/* Elementos decorativos adicionales */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-[#282828]/20 to-[#282828]/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-200/20 to-indigo-300/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-md w-full">
        {/* Card principal */}
        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl border border-white/30 p-8 space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="p-3 bg-gradient-to-br from-[#282828] to-[#282828] rounded-2xl shadow-lg">
                <Logo width={100} height={35} />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Panel de Administración
            </h2>
            <p className="text-sm text-gray-600">
              Inicia sesión para acceder al panel de administración
            </p>
          </div>

          {/* Formulario */}
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-5">
              {/* Campo Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail
                      className={`h-5 w-5 transition-colors ${
                        errors.email ? "text-red-400" : "text-gray-400"
                      }`}
                    />
                  </div>
                  <input
                    {...register("email")}
                    type="email"
                    autoComplete="email"
                    className={`block w-full pl-12 pr-4 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-opacity-30 transition-all duration-200 sm:text-sm ${
                      errors.email
                        ? "border-red-300 focus:ring-red-500 focus:border-red-400 bg-red-50"
                        : "border-gray-200 focus:ring-[#282828] focus:border-[#282828] hover:border-gray-300"
                    }`}
                    placeholder="tu@email.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Campo Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock
                      className={`h-5 w-5 transition-colors ${
                        errors.password ? "text-red-400" : "text-gray-400"
                      }`}
                    />
                  </div>
                  <input
                    {...register("password")}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    className={`block w-full pl-12 pr-12 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-opacity-30 transition-all duration-200 sm:text-sm ${
                      errors.password
                        ? "border-red-300 focus:ring-red-500 focus:border-red-400 bg-red-50"
                        : "border-gray-200 focus:ring-[#282828] focus:border-[#282828] hover:border-gray-300"
                    }`}
                    placeholder="Tu contraseña"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.password.message}
                  </p>
                )}
              </div>
            </div>

            {/* Error general */}
            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-600 text-xs">⚠️</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-red-800">
                      Error de autenticación
                    </h3>
                    <div className="mt-1 text-sm text-red-700">
                      <p>{error.message}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Botón de envío */}
            <div>
              <button
                type="submit"
                disabled={!isValid || isLoading}
                className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-xl focus:outline-none focus:ring-4 focus:ring-opacity-20 transition-all duration-200 transform ${
                  !isValid || isLoading
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    <span>Iniciando sesión...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <LogIn className="h-5 w-5 mr-3 transition-transform group-hover:translate-x-1" />
                    <span>Iniciar Sesión</span>
                  </div>
                )}
              </button>
            </div>

            {/* Enlace al sitio público */}
            <div className="text-center pt-4">
              <Link
                to="/"
                className="inline-flex items-center text-sm text-gray-600 hover:text-[#282828] transition-colors duration-200 group"
              >
                <span className="mr-2 transition-transform group-hover:-translate-x-1">
                  ←
                </span>
                Volver al sitio público
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
